import { useRouteMatch } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import {
  Col,
  Table,
  DatePicker,
  Form,
  Input,
  Row,
  Skeleton,
  Typography,
  Divider,
  Dropdown,
  Button,
  Menu,
} from 'antd';
import { ColumnsType } from 'antd/es/table/interface';

import { useStockCorrection } from '@/core/domain/stock/StockCorrections/StockCorrections.hooks';
import { StockCorrectionItem } from '@/core/domain/stock/StockCorrections/StockCorrections.types';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';

import { PageProps } from '@/types/PageProps';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { quantityFormatter } from '@/utils/number';
import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';
import useFileLoading, { FileType, FILE_FORMAT } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { useGenerateStockCorrectionReport } from '@/core/domain/reports/stock/stockCorrection/Report.hooks';
import StatusTag from '@/components/_common/StatusTag';
import { getTableScrollX } from '@/utils/table';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { ChevronDown } from 'react-feather';
import ButtonBack from '@/components/_common/ButtonBack';
import generateCsv from '@/utils/functions/generateCsv';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';

const { Title } = Typography;

function StockCorrectionDetailsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { stockCorrectionId } = useRouteMatch().params as {
    stockCorrectionId: number;
  };

  const { data, isFetching } = useStockCorrection(stockCorrectionId);
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useGenerateStockCorrectionReport();

  useUpdateBreadcrumbParam(
    ':stockCorrectionId',
    `${data?.result?.company.name}, ${
      data ? moment(data?.result?.date).format('DD/MM/YYYY') : null
    }`,
  );

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const result = await generateReport.mutateAsync({
        format: FILE_FORMAT[format],
        stockCorrectionId,
      });
      if (callback) {
        callback(result);
      }
    } catch {}
    setFileUnloading(format);
  };

  function viewReport() {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'correcao_de_estoque.pdf',
        fileType: 'application/pdf',
      }),
    );
  }

  const columnPart = useConfigurationColumnsPart({
    internalCode: 'sku',
    barcode: 'barCode',
  });

  const handleCsv = () => {
    const csvData = data?.result?.items?.map(
      ({
        itemName,
        oldQuantity,
        newQuantity,
        deltaQuantity,
        partId,
        ...rest
      }) => ({
        Código: rest[columnPart.dataIndex as keyof typeof rest] as number,
        Nome: itemName,
        Tipo: partId ? 'Peça' : 'Insumo',
        'Qtd. Anterior': quantityFormatter(oldQuantity),
        'Nova Qtd.': quantityFormatter(newQuantity),
        Diferença: quantityFormatter(deltaQuantity),
      }),
    );
    let csvTotal = {
      Código: '',
      Nome: '',
      Tipo: 'Total',
      'Qtd. Anterior': 0,
      'Nova Qtd.': 0,
      Diferença: 0,
    };
    csvTotal = data?.result?.items
      ? data.result.items.reduce((acc, curr) => {
          const { oldQuantity, newQuantity, deltaQuantity } = curr;
          acc['Qtd. Anterior'] += oldQuantity;
          acc['Nova Qtd.'] += newQuantity;
          acc['Diferença'] += deltaQuantity;
          return acc;
        }, csvTotal)
      : csvTotal;
    if (csvData) {
      return generateCsv(
        [
          ...csvData,
          {
            ...csvTotal,
            'Qtd. Anterior': quantityFormatter(csvTotal['Qtd. Anterior']),
            'Nova Qtd.': quantityFormatter(csvTotal['Nova Qtd.']),
            Diferença: quantityFormatter(csvTotal['Diferença']),
          },
        ],
        'Correção de estoque',
      );
    }
  };

  const reportOptions = (
    <Menu
      items={[
        {
          label: intl.formatMessage({ id: 'reports.button.pdf' }),
          key: 'pdf',
          onClick: () => {
            handleGenerateReport('PDF', (result) =>
              report.downloadFile({
                result,
                fileName: 'correcao_de_estoque.pdf',
              }),
            );
          },
          disabled: fileLoading.PDF,
        },
        {
          label: intl.formatMessage({ id: 'reports.button.csv' }),
          key: 'csv',
          onClick: handleCsv,
        },
      ]}
    />
  );

  const columns: ColumnsType<StockCorrectionItem> = [
    columnPart,
    {
      ellipsis: true,
      dataIndex: 'itemName',
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.itemName',
      }),
    },
    {
      ellipsis: true,
      width: 130,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.type',
      }),
      render(row: StockItem) {
        let label = '-';

        if (row.partId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.part',
          });
        } else if (row.consumableId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.consumable',
          });
        }

        return <StatusTag label={label} color="blue" />;
      },
    },
    {
      ellipsis: true,
      width: 130,
      dataIndex: 'oldQuantity',
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.oldQuantity',
      }),
      align: 'right',
      render: (value: number) => quantityFormatter(value),
    },
    {
      width: 130,
      dataIndex: 'newQuantity',
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.newQuantity',
      }),
      align: 'right',
      render: (value: number) => quantityFormatter(value),
    },
    {
      width: 130,
      dataIndex: 'deltaQuantity',
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.deltaQuantity',
      }),
      align: 'right',
      render: (value: number) => quantityFormatter(value),
    },
  ];

  return (
    <TabContentWrapper title={route.name} permissions={route.permissions}>
      <Form form={form}>
        <Skeleton active loading={isFetching}>
          <Row gutter={16}>
            <Col span={24} md={12}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'stockCorrections.domain.date',
                })}
              >
                <DatePicker
                  disabled
                  value={moment(data?.result?.date)}
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: '100%' }}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'stockCorrections.domain.employee',
                })}
              >
                <Input disabled value={data?.result?.employee.name} />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'stockCorrections.domain.company',
                })}
              >
                <Input disabled value={data?.result?.company.name} />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'stockCorrections.domain.observation',
                })}
              >
                <Input.TextArea
                  rows={4}
                  disabled
                  value={data?.result?.observation}
                />
              </FloatingFormItem>
            </Col>
          </Row>

          <div style={{ margin: '24px 0' }}>
            <Title level={3}>
              <FormattedMessage id="stockCorrections.domain.items" />
            </Title>
          </div>

          <Table
            size="middle"
            columns={columns}
            dataSource={data?.result?.items}
            rowKey={(row) =>
              JSON.stringify({
                partId: row.partId,
                consumableId: row.consumableId,
              })
            }
            scroll={{ x: getTableScrollX(columns) }}
            pagination={false}
          />

          <Divider />

          <SpaceForButton>
            <ButtonBack path="/app/movements/stockCorrections" />

            <Button.Group style={{ width: '100%' }}>
              <Button block style={{ width: '82%' }} onClick={viewReport}>
                Visualizar relatório
              </Button>

              <Dropdown
                overlay={reportOptions}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button style={{ padding: 0, paddingLeft: 5 }}>
                  <ChevronDown />
                </Button>
              </Dropdown>
            </Button.Group>
          </SpaceForButton>
        </Skeleton>
      </Form>
    </TabContentWrapper>
  );
}

export default StockCorrectionDetailsPage;
